<!-- PricingHourStrategyComponent.vue -->
<template>
  <form @submit.prevent>
    <div class="row mb-3" :style="{ opacity: isCalendarVisible ? 1 : 0 }">
      <div class="col-12">
        <div class="form-group">
          <div v-if="currentStrategy.isMultipleWindows">
            <PricingStrategyNameDaysComponent
              :name="windowProp.name"
              :description="windowProp.description"
              :daysIn="windowProp.daysIn"
              :daysOut="windowProp.daysOut"
              :invalidDaysIn="invalidDaysIn(windowProp.id)"
              :invalidDaysOut="invalidDaysOut(windowProp.id)"
              :daysInMoreThanDayOut="daysInMoreThanDayOut(windowProp.id)"
              :invalidName="invalidName(windowProp.id)"
              @updateWindow="handleUpdateWindowProp"
            />
          </div>
          <FullCalendar
            v-if="isCalendarVisible"
            :key="`calendar-${windowIndex}-${priceWindowId}`"
            :options="calendarOptions"
            :data-window-index="windowIndex"
            :ref="
              (el: any) => {
                fullCalendarRef = el;
              }
            "
          />

          <div class="button-group mt-3">
            <button
              class="btn btn-primary"
              type="button"
              @click="openCreateTimeWindowModal"
            >
              <i class="fa fa-plus"></i>
              Create New Time Window
            </button>
            <button
              class="btn btn-secondary"
              type="button"
              @click="openCopyDayModal"
            >
              <i class="fa fa-copy"></i>
              Copy Day
            </button>
          </div>
        </div>
      </div>
      <ManageTimeWindowModal
        :calendarOptions="calendarOptions"
        :initialData="initialDataForTimeManagement"
        :operationType="eventOperationType"
        :manageTimeWindowModalRef="manageTimeWindowModalRef"
        @manage-time-window-result="handleManageTimeWindowResult"
        @set-modal-ref="manageTimeWindowModalRef = $event"
        v-model:utilizationPriceSettingsRef="utilizationPriceSettingsRef"
        :currentStrategy="currentStrategy"
      />
      <div v-if="isCopyDayModalOpen">
        <div class="copy-day-modal-backdrop" @click="closeCopyDayModal"></div>
        <CopyDayModal
          :initialData="initialData"
          @cancel="closeCopyDayModal"
          @copy="handleCopyDay"
        />
      </div>
    </div>
    <ConfirmModal
      :show="showOverwriteConfirmModal"
      message="Warning: Data Will Be Overwritten"
      prompt="This action will permanently delete the existing data and replace it with the copied content. This cannot be undone. Do you want to continue?"
      confirmText="Continue"
      cancelText="Cancel"
      confirmType="confirm-modal-btn-primary"
      cancelType="confirm-modal-btn-danger"
      @confirm="onOverwriteConfirm"
    />
  </form>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  VNodeRef,
  ComponentPublicInstance,
  onMounted,
  nextTick,
  onBeforeUnmount,
} from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  CalendarOptions,
  EventClickArg,
  EventContentArg,
} from '@fullcalendar/core';
import { UtilizationPrice, Strategy } from '@/api';
import ManageTimeWindowModal from './ManageTimeWindowModal.vue';
import PricingStrategyNameDaysComponent from './PricingStrategyNameDaysComponent.vue';
import { Modal } from 'bootstrap';
import CopyDayModal from './CopyDayModal.vue';
import ConfirmModal from '../ConfirmModal.vue';
import { DayName } from '../../types/dayNameType';
import { useToast } from 'vue-toastification';
import {
  dynPricingDayOfWeek,
  fullCalendarDayOfWeek,
  getDefaultOneHourWindowLocal,
} from '@/utils/dateUtils';
import { useCalendarDataTransform } from '@/composables/useCalendarDataTransform';
import { useTimeWindowOverlap } from '@/composables/useTimeWindowOverlap';
import { useTimeWindowEdit } from '@/composables/useTimeWindowEdit';
import { usePriceWindowPersistence } from '@/composables/usePriceWindowPersistence';

interface TimeManagementData {
  startTime: string;
  endTime: string;
  extendedProps?: any;
  title?: string;
  id?: string;
}

interface PendingEditData {
  result: any;
  sourceRanges: UtilizationPrice[];
  sourceDay: number;
  sourceDayIncluded: boolean;
  daysWithoutOverlaps: number[];
  daysWithOverlaps: number[];
}

export default defineComponent({
  components: {
    FullCalendar,
    ManageTimeWindowModal,
    CopyDayModal,
    PricingStrategyNameDaysComponent,
    ConfirmModal,
  },
  name: 'PricingHourStrategyComponent',
  emits: [
    'addUtilizationPrice',
    'updateInitialData',
    'save-success',
    'save-error',
    'updateWindow',
  ],
  props: {
    windowProp: {
      type: Object,
      required: true,
    },
    windowIndex: {
      type: Number,
      required: true,
    },
    initialData: {
      type: Array as PropType<UtilizationPrice[]>,
      default: () => [],
    },
    priceWindowId: {
      type: Number as PropType<number | undefined>,
      required: true,
    },
    currentStrategy: {
      type: Object as PropType<Strategy>,
      required: true,
    },
    invalidDaysIn: {
      type: Function,
      required: true,
    },
    invalidDaysOut: {
      type: Function,
      required: true,
    },
    daysInMoreThanDayOut: {
      type: Function,
      required: true,
    },
    invalidName: {
      type: Function,
      required: true,
    },
  },
  setup(props, { emit }) {
    const toast = useToast();
    const DEFAULT_START = '1900-01-01T00:00:00';
    const DEFAULT_END = '1900-01-01T23:59:59';

    // State
    const initialDataForTimeManagement = ref<TimeManagementData>(
      {} as TimeManagementData
    );
    const eventOperationType = ref('');
    const lastTempId = ref(-1);
    const isCalendarVisible = ref(false);
    const manageTimeWindowModalRef = ref<VNodeRef | null>({} as VNodeRef);
    const utilizationPriceSettingsRef = ref<ComponentPublicInstance | null>();
    const isCopyDayModalOpen = ref(false);
    const workingData = ref<UtilizationPrice[]>(
      JSON.parse(JSON.stringify(props.initialData))
    );
    const fullCalendarRef = ref<typeof FullCalendar | null>(null);
    const showOverwriteConfirmModal = ref(false);
    const pendingEditResult = ref<PendingEditData | null>(null);

    const CALENDAR_SLOT_MIN = '01:00:00';
    const CALENDAR_SLOT_MAX = '22:00:00';

    const validHours = {
      daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
      startTime: '01:00',
      endTime: '22:00',
    };

    // Calculate Monday of current week
    const monday = (() => {
      const today = new Date();
      const daysSinceMonday = (today.getDay() + 6) % 7;
      const mondayDate = new Date(today);
      mondayDate.setDate(today.getDate() - daysSinceMonday);
      return mondayDate;
    })();

    // Composables
    const { calendarEvents, formatDate } = useCalendarDataTransform(
      workingData,
      monday,
      lastTempId
    );
    const { isOverlapping, isWithinValidHours } = useTimeWindowOverlap();
    const {
      handleCreateTimeWindow,
      validateEditOverlaps,
      applySourceDayEdit,
      copyToTargetDays,
      handleDeleteTimeWindow,
      handleCopyTimeWindow,
    } = useTimeWindowEdit(
      workingData,
      lastTempId,
      props.priceWindowId,
      isOverlapping
    );
    const { saveUtilizationPrices } = usePriceWindowPersistence();

    const openCreateTimeWindowModal = () => {
      const { start, end } = getDefaultOneHourWindowLocal(
        CALENDAR_SLOT_MIN,
        CALENDAR_SLOT_MAX
      );
      handleSelect({ start, end });
    };

    const manageTimeWindowModalInstance = computed(() => {
      if (!manageTimeWindowModalRef.value) return null;
      return (
        Modal.getInstance(manageTimeWindowModalRef.value) ||
        new Modal(manageTimeWindowModalRef.value)
      );
    });

    watch(
      () => props.initialData,
      (newData) => {
        const raw = JSON.parse(JSON.stringify(newData));
        workingData.value = (raw || []).map((item: UtilizationPrice) => ({
          ...item,
          startTime: item.startTime ?? DEFAULT_START,
          endTime: item.endTime ?? DEFAULT_END,
        }));
      },
      { deep: true }
    );

    const handleSelect = (info: any) => {
      if (manageTimeWindowModalInstance.value) {
        const eventInitialData = {
          startTime: info.start.toTimeString().split(' ')[0],
          endTime: info.end.toTimeString().split(' ')[0],
          extendedProps: {
            dayOfWeek: dynPricingDayOfWeek(info.start.getDay()),
            utilizationRanges: [
              {
                low: 0,
                high: 100,
                pricePercentage: 0,
                priceAmount: 0,
                id: 0,
                usePercent: true,
                priceWindowId: props.priceWindowId,
                dayOfTheWeek: info.start.getDay(),
                startTime: info.start.toTimeString().split(' ')[0],
                endTime: info.end.toTimeString().split(' ')[0],
                timeWindowDescription: '',
              },
            ],
          },
        };

        initialDataForTimeManagement.value = eventInitialData;
        eventOperationType.value = 'create';

        manageTimeWindowModalInstance.value.show();
      }
    };

    const selectAllow = (selectInfo: any) => {
      return selectInfo.start.getDate() === selectInfo.end.getDate();
    };

    const findEventById = (eventId: string | null) => {
      if (!eventId) return null;
      return calendarEvents.value.find((e) => e.id == eventId);
    };

    const calendarOptions = computed<CalendarOptions>(() => ({
      plugins: [timeGridPlugin, interactionPlugin],
      initialView: 'timeGridWeek',
      initialDate: formatDate(monday),
      firstDay: 1,
      slotMinTime: CALENDAR_SLOT_MIN,
      slotMaxTime: CALENDAR_SLOT_MAX,
      allDaySlot: false,
      headerToolbar: false,
      dayHeaderFormat: { weekday: 'long' },
      slotDuration: '01:00:00',
      contentHeight: 'auto',
      editable: true,
      selectable: true,
      eventOverlap: false,
      events: calendarEvents.value ?? [],
      eventClick: (arg: EventClickArg) => {
        if (arg.event.id) {
          const event = findEventById(arg.event.id);
          if (event) {
            let targetElement = arg.jsEvent.target as HTMLElement;

            // Traverse up the DOM tree to find the action button
            while (
              targetElement &&
              !targetElement.classList.contains('copy-btn') &&
              !targetElement.classList.contains('edit-btn') &&
              !targetElement.classList.contains('delete-btn') &&
              targetElement !== arg.jsEvent.currentTarget
            ) {
              targetElement = targetElement.parentElement as HTMLElement;
            }

            // Determine the operation based on the button clicked
            if (targetElement) {
              if (targetElement.classList.contains('copy-btn')) {
                eventOperationType.value = 'copy';
              } else if (targetElement.classList.contains('edit-btn')) {
                eventOperationType.value = 'edit';
              } else if (targetElement.classList.contains('delete-btn')) {
                eventOperationType.value = 'delete';
              } else {
                eventOperationType.value = 'edit';
              }
            } else {
              eventOperationType.value = 'create';
            }

            const endTime = new Date(event.end);
            const endTimeStr = endTime.toTimeString().split(' ')[0];
            const eventInitialData = {
              startTime: event.start.split('T')[1],
              endTime: endTimeStr,
              extendedProps: event.extendedProps,
              title: event.title,
              id: event.id,
            };
            initialDataForTimeManagement.value = eventInitialData;

            manageTimeWindowModalInstance.value?.show();
          }
        }
      },
      eventContent: (arg: EventContentArg) => {
        const { event } = arg;
        const { extendedProps } = event;

        const eventDurationInHours =
          new Date(event.end!).getHours() - new Date(event.start!).getHours();

        const priceDisplay =
          eventDurationInHours > 1
            ? extendedProps.utilizationRanges
                .map((util: UtilizationPrice) => {
                  const utilization = props.currentStrategy
                    .isAdjustByUtilization
                    ? `Utilization: ${util.low}-${util.high}: `
                    : '';
                  const price = util.usePercent
                    ? `${util.pricePercentage}%`
                    : `$${util.priceAmount}`;
                  const display = `${utilization} ${price}`;
                  return `<div class="fc-event-price event-content-align">${display}</div>`;
                })
                .join('')
            : '';

        // Format start and end times using local time
        const startTime = new Date(event.start!).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        });
        const endTime = new Date(event.end!).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        });

        const eventTimeDisplay =
          eventDurationInHours > 1
            ? `<div class="fc-event-time event-content-align">${startTime} - ${endTime}</div>`
            : '';

        return {
          html: `
            <div class="fc-event-main-frame">
              <div class="fc-event-title-container">
                <div class="event-actions event-actions-align">
                  <i class="fa fa-copy copy-btn" style="cursor:pointer;"></i>
                  <i class="fa fa-edit edit-btn" style="cursor:pointer;"></i>
                  <i class="fa fa-trash delete-btn" style="cursor:pointer;"></i>
                </div>
                <div class="fc-event-title event-title-align">${event.title}</div>
              </div>
              ${eventTimeDisplay}
              ${priceDisplay}
            </div>
          `,
        };
      },
      select: handleSelect,
      selectAllow: selectAllow,
      eventResize: handleResize,
      eventDrop: handleResize,
    }));

    const openCopyDayModal = () => {
      isCopyDayModalOpen.value = true;
    };

    const closeCopyDayModal = () => {
      isCopyDayModalOpen.value = false;
    };

    const handleResize = (info: any) => {
      try {
        const currentEvent = findEventById(info.event.id);

        if (currentEvent) {
          if (
            isWithinValidHours(info.event.start, info.event.end, validHours)
          ) {
            const options = { hour12: false };
            const updatedEvent = {
              ...currentEvent,
              dayOfWeek: info.event.start.getDay(),
              start: `1900-01-01T${info.event.start.toLocaleTimeString(
                [],
                options
              )}`,
              end: `1900-01-01T${info.event.end.toLocaleTimeString(
                [],
                options
              )}`,
            };

            const util = workingData.value.filter(
              (item) =>
                [
                  fullCalendarDayOfWeek(item.dayOfTheWeek!),
                  item.startTime,
                  item.endTime,
                ].join('|') === currentEvent.id
            );

            if (util) {
              util.forEach((item) => {
                item.dayOfTheWeek = dynPricingDayOfWeek(updatedEvent.dayOfWeek);
                item.startTime = updatedEvent.start;
                item.endTime = updatedEvent.end;
                handleEditEvent(item);
              });

              currentEvent.id = [
                fullCalendarDayOfWeek(updatedEvent.dayOfWeek!),
                updatedEvent.start,
                updatedEvent.end,
              ].join('|');
            }
          } else {
            info.revert();
            return;
          }
        }
      } catch (error) {
        toast.error('An error occurred while resizing the event.');
      }
    };

    const handleCopyDay = async (payload: { from: DayName; to: DayName[] }) => {
      try {
        const dayNameToNumber: Record<DayName, number> = {
          Monday: 1,
          Tuesday: 2,
          Wednesday: 3,
          Thursday: 4,
          Friday: 5,
          Saturday: 6,
          Sunday: 7,
        };

        const fromDayNumber = dayNameToNumber[payload.from];
        const toDayNumbers = payload.to.map(
          (day: DayName) => dayNameToNumber[day]
        );

        const fromData = workingData.value.filter(
          (item) => item.dayOfTheWeek === fromDayNumber
        );

        if (fromData.length === 0) {
          toast.error(`No data found for ${payload.from}`);
          return;
        }

        let newUtilizationPrices: UtilizationPrice[] = [];

        toDayNumbers.forEach((dayNumber) => {
          const copiedData = fromData.map((item) => ({
            ...item,
            dayOfTheWeek: dayNumber,
            id: lastTempId.value--,
          }));
          newUtilizationPrices = newUtilizationPrices.concat(copiedData);
        });

        const existingDataExcludingToDays = workingData.value.filter(
          (item) => !toDayNumbers.includes(item.dayOfTheWeek!)
        );

        workingData.value =
          existingDataExcludingToDays.concat(newUtilizationPrices);

        closeCopyDayModal();
      } catch (error) {
        toast.error(`Error copying data: ${error}`);
        alert('An error occurred while copying the data.');
      }
    };

    const handleCreateEvent = (newEvent: UtilizationPrice) => {
      workingData.value = [...workingData.value, newEvent];
    };

    const handleEditEvent = (updatedEvent: UtilizationPrice) => {
      workingData.value = workingData.value.map((event) =>
        event.id === updatedEvent.id ? updatedEvent : event
      );
    };

    const handleDeleteEvent = (eventId: number) => {
      workingData.value = workingData.value.filter(
        (event) => event.id !== eventId
      );
    };

    const handleManageTimeWindowResult = (result: any) => {
      const handlers: Record<string, (res: any) => void> = {
        create: (res: any) => handleCreateTimeWindow(res),
        edit: (res: any) => {
          const sourceDay = dynPricingDayOfWeek(
            initialDataForTimeManagement.value.extendedProps.dayOfWeek
          );
          const sourceStartTime = initialDataForTimeManagement.value.startTime;
          const sourceEndTime = initialDataForTimeManagement.value.endTime;

          const sourceRanges = workingData.value.filter(
            (data) =>
              data.dayOfTheWeek === sourceDay &&
              data.startTime === `1900-01-01T${sourceStartTime}` &&
              data.endTime === `1900-01-01T${sourceEndTime}`
          );

          const targetDays = res.events.map((e: any) =>
            dynPricingDayOfWeek(e.dayOfWeek ?? 0)
          );
          const sourceDayIncluded = targetDays.includes(sourceDay);

          const validation = validateEditOverlaps(
            res,
            sourceDay,
            sourceRanges,
            sourceDayIncluded
          );

          if (validation.daysWithOverlaps.length > 0) {
            pendingEditResult.value = {
              result: res,
              sourceRanges,
              sourceDay,
              sourceDayIncluded,
              daysWithoutOverlaps: validation.daysWithoutOverlaps,
              daysWithOverlaps: validation.daysWithOverlaps,
            };
            showOverwriteConfirmModal.value = true;
            return;
          }

          const sourceDayEvent = res.events.find(
            (e: any) => dynPricingDayOfWeek(e.dayOfWeek ?? 0) === sourceDay
          );
          if (sourceDayEvent?.timeWindows?.[0]) {
            applySourceDayEdit(
              sourceDayEvent.timeWindows[0],
              sourceDay,
              sourceRanges,
              false
            );
          }
          copyToTargetDays(res, validation.daysWithoutOverlaps, [], sourceDay);
        },
        delete: (res: any) => handleDeleteTimeWindow(res),
        copy: (res: any) => handleCopyTimeWindow(res),
      };
      handlers[result.operationType]?.(result);
    };

    const onOverwriteConfirm = (confirmed: boolean) => {
      showOverwriteConfirmModal.value = false;

      if (!pendingEditResult.value) return;

      if (!confirmed) {
        pendingEditResult.value = null;
        return;
      }

      const {
        result,
        sourceRanges,
        sourceDay,
        sourceDayIncluded,
        daysWithoutOverlaps,
        daysWithOverlaps,
      } = pendingEditResult.value;

      const sourceDayEvent = result.events.find(
        (e: any) => dynPricingDayOfWeek(e.dayOfWeek ?? 0) === sourceDay
      );

      if (sourceDayIncluded && sourceDayEvent?.timeWindows?.[0]) {
        const shouldOverwrite = daysWithOverlaps.includes(sourceDay);
        applySourceDayEdit(
          sourceDayEvent.timeWindows[0],
          sourceDay,
          sourceRanges,
          shouldOverwrite
        );
      }
      copyToTargetDays(
        result,
        [...daysWithoutOverlaps, ...daysWithOverlaps],
        daysWithOverlaps,
        sourceDay
      );

      pendingEditResult.value = null;
    };

    const triggerSave = async (newWindowId = null) => {
      try {
        await saveChanges(newWindowId);
      } catch (error) {
        emit('save-error', error);
        throw error;
      }
    };

    const saveChanges = async (newWindowId = null) => {
      try {
        await saveUtilizationPrices(
          workingData.value,
          newWindowId ?? props.priceWindowId
        );
        emit('updateInitialData', workingData.value);
        emit('save-success');
      } catch (error) {
        toast.error(`Failed to save changes: ${error}`);
        emit('save-error', error);
      }
    };

    const handleUpdateWindowProp = (
      key: 'name' | 'description' | 'daysIn' | 'daysOut',
      value: string | number
    ) => {
      emit('updateWindow', props.windowProp, key, value);
    };

    const toggleResingCalendar = () => {
      isCalendarVisible.value = true;
      nextTick(() => {
        fullCalendarRef.value?.getApi?.()?.updateSize();
      });
    };

    const handleAccordionShow = () => {
      isCalendarVisible.value = false;
    };

    onMounted(() => {
      const collapseElement = document.getElementById(
        `collapse${props.windowIndex}`
      );
      if (collapseElement) {
        collapseElement.addEventListener(
          'show.bs.collapse',
          handleAccordionShow
        );
        collapseElement.addEventListener(
          'shown.bs.collapse',
          toggleResingCalendar
        );
      }
      toggleResingCalendar();
    });

    onBeforeUnmount(() => {
      const collapseElement = document.getElementById(
        `collapse${props.windowIndex}`
      );
      if (collapseElement) {
        collapseElement.removeEventListener(
          'show.bs.collapse',
          handleAccordionShow
        );
        collapseElement.removeEventListener(
          'shown.bs.collapse',
          toggleResingCalendar
        );
      }
    });

    return {
      fullCalendarRef,
      isCalendarVisible,
      calendarOptions,
      initialDataForTimeManagement,
      eventOperationType,
      handleManageTimeWindowResult,
      isCopyDayModalOpen,
      openCopyDayModal,
      closeCopyDayModal,
      handleCopyDay,
      manageTimeWindowModalRef,
      utilizationPriceSettingsRef,
      handleCreateEvent,
      handleEditEvent,
      handleDeleteEvent,
      saveChanges,
      openCreateTimeWindowModal,
      triggerSave,
      handleUpdateWindowProp,
      showOverwriteConfirmModal,
      onOverwriteConfirm,
    };
  },
});
</script>

<style scoped>
.form-group label {
  font-weight: 600;
}

.button-group {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.btn-primary {
  background-color: #405f6b;
  border-color: #405f6b;
}

.btn-primary:hover,
.btn-primary:focus {
  background-color: #618183;
  border-color: #618183;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.btn-secondary:hover,
.btn-secondary:focus {
  background-color: #5a6268;
  border-color: #545b62;
}

.copy-day-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.copy-day-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

body.modal-open {
  overflow: hidden;
}
</style>
<style>
.event-title-align {
  float: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: calc(100% - 50px);
}

.event-content-align {
  float: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: calc(100% - 10px);
}

.event-actions-align {
  float: right;
}
</style>
